import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import swal from 'sweetalert';
import LoadingBar from 'react-top-loading-bar';
import { useIsOnline } from 'react-use-is-online';
import { useCreateUserEmployeeMutation } from '../../Features/userEmployeesSlice';
import { selectCurrentUser } from '../../Features/auth/authSlice';
import { useSelector } from 'react-redux';
import { useGetUserEmployeeRolesByUserIdQuery } from '../../Features/usersSlice';
import { useTheme } from '../../ThemeContext';

import {
    UserAddIcon,
    UserIcon,
    MailIcon,
    PhoneIcon,
    LockClosedIcon,
    BriefcaseIcon,
    ArrowLeftIcon,
} from '@heroicons/react/outline';

function UserEmployeeRegisterView() {
    const navigate = useNavigate();
    const { isOnline } = useIsOnline();
    const { darkMode } = useTheme();

    const user = useSelector(selectCurrentUser);

    const [progress, setProgress] = useState(0);

    const [input, setInput] = useState({
        email: '',
        phone: '',
        fname: '',
        lname: '',
        password: '',
        role_id: '',
        account_id: user.id,
    });

    const [errors, setErrors] = useState({
        email: '',
        phone: '',
        fname: '',
        lname: '',
        role_id: '',
        password: '',
    });

    const {
        data: employeeRoles,
        isLoading: rolesLoading,
        isSuccess: rolesSuccess,
    } = useGetUserEmployeeRolesByUserIdQuery(user.id);

    const [register, { isLoading: registerLoading }] =
        useCreateUserEmployeeMutation();

    const handleChange = (e) => {
        const { name, value } = e.target;

        setInput((prev) => ({
            ...prev,
            [name]: value,
        }));

        // Clear the field error while typing
        setErrors((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isOnline) {
            return swal(
                'No Internet connection',
                'Please check Internet connection and try again',
                'error'
            );
        }

        setProgress(80);

        try {
            const resp = await register(input).unwrap();

            if (resp.success === true) {
                setProgress(100);
                navigate(-1);
            }
        } catch (err) {
            setProgress(100);

            if (err?.data?.errors) {
                setErrors({
                    email: err.data.errors.email || '',
                    phone: err.data.errors.phone || '',
                    fname: err.data.errors.fname || '',
                    lname: err.data.errors.lname || '',
                    role_id: err.data.errors.role_id || '',
                    password: err.data.errors.password || '',
                });
            } else if (err?.data?.error) {
                return swal(
                    'Registration Failed',
                    err.data.error,
                    'error'
                );
            } else {
                return swal(
                    'Registration Failed',
                    'Something went wrong. Please try again.',
                    'error'
                );
            }
        }
    };

    const back = () => {
        if (!isOnline) {
            return swal(
                'No Internet connection',
                'Please check Internet connection and try again',
                'error'
            );
        }

        setProgress(100);
        navigate(-1);
    };

    useEffect(() => {
        if (user?.id) {
            setInput((prev) => ({
                ...prev,
                account_id: user.id,
            }));
        }
    }, [user?.id]);

    const inputClass =
        'w-full px-3 py-2.5 rounded-lg bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-white border border-gray-200 dark:border-gray-700 focus:border-[#80b643] focus:ring-1 focus:ring-[#80b643] outline-none transition';

    return (
        <div className="w-full min-h-screen bg-gray-100 dark:bg-gray-950 pb-12">
            <LoadingBar
                color="#80b643"
                height="4px"
                progress={progress}
                onLoaderFinished={() => setProgress(0)}
            />

            <div className="w-full px-4 sm:px-6 lg:px-8 pt-6">
                {/* PAGE HEADER */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                            <UserAddIcon className="w-6 h-6 text-[#80b643]" />
                        </div>

                        <div>
                            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                                Add Employee
                            </h1>

                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Create a new employee account
                            </p>
                        </div>
                    </div>
                </div>

                {/* FORM CARD */}
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">
                    {/* CARD HEADER */}
                    <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-800">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                                <UserIcon className="w-5 h-5 text-[#80b643]" />
                            </div>

                            <div>
                                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                                    Employee Information
                                </h2>

                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    Enter the employee's account details below
                                </p>
                            </div>
                        </div>
                    </div>

                    <form
                        onSubmit={handleSubmit}
                        autoComplete="off"
                        className="p-4 sm:p-6"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {/* FIRST NAME */}
                            <div>
                                <label
                                    htmlFor="fname"
                                    className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    <UserIcon className="w-4 h-4 text-[#80b643]" />
                                    First name
                                </label>

                                <input
                                    type="text"
                                    name="fname"
                                    id="fname"
                                    value={input.fname}
                                    onChange={handleChange}
                                    className={inputClass}
                                    placeholder="Enter first name"
                                />

                                {errors.fname && (
                                    <p className="mt-1.5 text-xs font-medium text-red-500">
                                        {errors.fname}
                                    </p>
                                )}
                            </div>

                            {/* LAST NAME */}
                            <div>
                                <label
                                    htmlFor="lname"
                                    className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    <UserIcon className="w-4 h-4 text-[#80b643]" />
                                    Last name
                                </label>

                                <input
                                    type="text"
                                    name="lname"
                                    id="lname"
                                    value={input.lname}
                                    onChange={handleChange}
                                    className={inputClass}
                                    placeholder="Enter last name"
                                />

                                {errors.lname && (
                                    <p className="mt-1.5 text-xs font-medium text-red-500">
                                        {errors.lname}
                                    </p>
                                )}
                            </div>

                            {/* EMAIL */}
                            <div>
                                <label
                                    htmlFor="email"
                                    className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    <MailIcon className="w-4 h-4 text-[#80b643]" />
                                    Email
                                </label>

                                <input
                                    type="email"
                                    name="email"
                                    id="email"
                                    value={input.email}
                                    onChange={handleChange}
                                    className={inputClass}
                                    placeholder="Enter email address"
                                />

                                {errors.email && (
                                    <p className="mt-1.5 text-xs font-medium text-red-500">
                                        {errors.email}
                                    </p>
                                )}
                            </div>

                            {/* PHONE */}
                            <div>
                                <label
                                    htmlFor="phone"
                                    className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    <PhoneIcon className="w-4 h-4 text-[#80b643]" />
                                    Phone number
                                </label>

                                <input
                                    type="text"
                                    name="phone"
                                    id="phone"
                                    value={input.phone}
                                    onChange={handleChange}
                                    className={inputClass}
                                    placeholder="Enter phone number"
                                />

                                {errors.phone && (
                                    <p className="mt-1.5 text-xs font-medium text-red-500">
                                        {errors.phone}
                                    </p>
                                )}
                            </div>

                            {/* ROLE */}
                            <div>
                                <label
                                    htmlFor="role_id"
                                    className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    <BriefcaseIcon className="w-4 h-4 text-[#80b643]" />
                                    Role
                                </label>

                                <select
                                    value={input.role_id}
                                    name="role_id"
                                    id="role_id"
                                    onChange={handleChange}
                                    disabled={rolesLoading}
                                    className={`${inputClass} ${
                                        rolesLoading
                                            ? 'opacity-60 cursor-not-allowed'
                                            : ''
                                    }`}
                                >
                                    <option value="">
                                        {rolesLoading
                                            ? 'Loading roles...'
                                            : 'Select role'}
                                    </option>

                                    {rolesSuccess &&
                                        employeeRoles?.data?.map((data) => (
                                            <option
                                                key={data.id}
                                                value={data.id}
                                            >
                                                {data.name}
                                            </option>
                                        ))}
                                </select>

                                {errors.role_id && (
                                    <p className="mt-1.5 text-xs font-medium text-red-500">
                                        {errors.role_id}
                                    </p>
                                )}
                            </div>

                            {/* PASSWORD */}
                            <div>
                                <label
                                    htmlFor="password"
                                    className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    <LockClosedIcon className="w-4 h-4 text-[#80b643]" />
                                    Password
                                </label>

                                <input
                                    type="number"
                                    name="password"
                                    id="password"
                                    value={input.password}
                                    onChange={handleChange}
                                    className={inputClass}
                                    placeholder="Enter password"
                                />

                                {errors.password && (
                                    <p className="mt-1.5 text-xs font-medium text-red-500">
                                        {errors.password}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* ACTIONS */}
                        <div className="mt-8 pt-5 border-t border-gray-200 dark:border-gray-800">
                            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={back}
                                    disabled={registerLoading}
                                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 w-full sm:w-auto disabled:opacity-50"
                                >
                                    <ArrowLeftIcon className="w-4 h-4" />
                                    Cancel
                                </button>

                                <button
                                    type="submit"
                                    disabled={registerLoading}
                                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#80b643] hover:bg-[#719f3a] text-white text-sm font-medium shadow-sm transition-all duration-200 w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    {registerLoading ? (
                                        <>
                                            <svg
                                                className="animate-spin h-4 w-4 text-white"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                ></circle>

                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                                ></path>
                                            </svg>

                                            Saving...
                                        </>
                                    ) : (
                                        'Save Employee'
                                    )}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default UserEmployeeRegisterView;